import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  TrendingUp,
  Filter,
  MousePointerClick,
  AlertTriangle,
  GitBranch,
  Clock,
  HelpCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type TabValue = "workflow" | "features" | "metrics" | "troubleshooting";

export function UserGuide() {
  const [activeTab, setActiveTab] = useState<TabValue>("workflow");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">User Guide</h1>
        <p className="text-muted-foreground mt-2">
          Guide to Loop’s license plate analytics
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        <Button
          variant={activeTab === "workflow" ? "default" : "outline"}
          onClick={() => setActiveTab("workflow")}
        >
          Daily Workflow
        </Button>
        <Button
          variant={activeTab === "features" ? "default" : "outline"}
          onClick={() => setActiveTab("features")}
        >
          Features
        </Button>
        <Button
          variant={activeTab === "metrics" ? "default" : "outline"}
          onClick={() => setActiveTab("metrics")}
        >
          Understanding Data
        </Button>
        <Button
          variant={activeTab === "troubleshooting" ? "default" : "outline"}
          onClick={() => setActiveTab("troubleshooting")}
        >
          Troubleshooting
        </Button>
      </div>

      {/* DAILY WORKFLOW TAB */}
      {activeTab === "workflow" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                <CardTitle>Daily Operations Workflow</CardTitle>
              </div>
              <CardDescription>
                Your step-by-step guide to daily parking data processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 1</Badge>
                  <h3 className="text-lg font-semibold">
                    Download Daily CSV Files
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Download entry and exit CSV files from your LPR camera system
                  for the day.
                </p>
                <div className="bg-muted p-3 rounded text-sm">
                  <strong>Expected Format:</strong> Each CSV should have columns
                  for License Plate, Camera ID, Timestamp, Location ID, and
                  Confidence (0-100).
                </div>
              </div>

              {/* Step 2 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 2</Badge>
                  <h3 className="text-lg font-semibold">Upload Files</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Navigate to the <strong>Upload</strong> page and upload your
                  CSV files.
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Select "Entry" or "Exit" type</li>
                  <li>Choose your CSV file</li>
                  <li>Click "Upload and Process"</li>
                  <li>
                    Watch real-time progress updates (Phase 1 → Phase 2 →
                    Complete)
                  </li>
                </ul>
                <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded text-sm mt-2">
                  <strong>💡 Pro Tip:</strong> The system automatically handles
                  out-of-order uploads with 7-day lookback. If you upload
                  Thursday before Wednesday, it will still match correctly.
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 3</Badge>
                  <h3 className="text-lg font-semibold">Automatic Matching</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  The system automatically matches entries with exits using a
                  two-phase approach:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>
                    <strong>Exact Matching:</strong> Same license plate, same
                    location, within 7-day window
                  </li>
                  <li>
                    <strong>Fuzzy Matching:</strong> Character confusion
                    handling (O↔0, I↔1, etc.) for remaining orphans
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground mt-2">
                  Processing typically takes 5-10 seconds for daily uploads, 2-5
                  minutes for multi-month backfills.
                </p>
              </div>

              {/* Step 4 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 4</Badge>
                  <h3 className="text-lg font-semibold">Review Dashboard</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Navigate to the <strong>Dashboard</strong> to review key
                  metrics:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>
                    <strong>Total Sessions:</strong> Number of matched
                    entry-exit pairs
                  </li>
                  <li>
                    <strong>Match Rate:</strong> Percentage of all records that
                    formed sessions
                  </li>
                  <li>
                    <strong>Orphan Entries:</strong> Entries without exits
                    (vehicles still parked or exit missed)
                  </li>
                  <li>
                    <strong>Orphan Exits:</strong> Exits without entries (entry
                    missed)
                  </li>
                </ul>
              </div>

              {/* Step 5 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 5</Badge>
                  <h3 className="text-lg font-semibold">Investigate Issues</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Check the <strong>Problem Plates</strong> section on the
                  Dashboard:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>
                    Plates with 5+ orphaned events indicate camera or
                    recognition issues
                  </li>
                  <li>Export the list for field investigation</li>
                  <li>
                    Coordinate with maintenance teams to fix problematic cameras
                  </li>
                </ul>
              </div>

              {/* Step 6 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="default">Step 6</Badge>
                  <h3 className="text-lg font-semibold">Export Reports</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Generate exports for reporting or further analysis:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>
                    <strong>Sessions CSV:</strong> All matched sessions with
                    entry/exit details
                  </li>
                  <li>
                    <strong>Problem Plates CSV:</strong> Recurring issues for
                    maintenance action
                  </li>
                  <li>
                    Use date range and zone filters before exporting for
                    targeted reports
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* FEATURES TAB */}
      {activeTab === "features" && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                <CardTitle>Global Filters</CardTitle>
              </div>
              <CardDescription>
                Filter data across all pages by date range and location
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Date Range Filter</h4>
                <p className="text-sm text-muted-foreground">
                  Select a date range to focus on specific time periods. The
                  filter applies to:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Dashboard KPIs and charts</li>
                  <li>Sessions list</li>
                  <li>Orphan entries and exits</li>
                  <li>Performance trends</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Zone (Location) Filter</h4>
                <p className="text-sm text-muted-foreground">
                  Select one or more zones to focus on specific parking
                  locations. Filters apply system-wide.
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded text-sm">
                <strong>💡 Pro Tip:</strong> Set your filters once in the top
                bar - they persist as you navigate between pages.
              </div>
            </CardContent>
          </Card>

          {/* Drill-Down */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <MousePointerClick className="h-5 w-5" />
                <CardTitle>Drill-Down Navigation</CardTitle>
              </div>
              <CardDescription>
                Click through from summary to detailed views
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Dashboard → Sessions</h4>
                <p className="text-sm text-muted-foreground">
                  Click any KPI card on the Dashboard to view the full Sessions
                  list with applied filters.
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">
                  Sessions → Session Details
                </h4>
                <p className="text-sm text-muted-foreground">
                  Click the "eye" icon on any session to see complete details
                  including:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Entry and exit timestamps</li>
                  <li>Duration calculation</li>
                  <li>Camera IDs used</li>
                  <li>Confidence scores</li>
                  <li>Match type (Exact or Fuzzy)</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Performance Trends */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                <CardTitle>Performance Trends</CardTitle>
              </div>
              <CardDescription>
                Track operational metrics over time
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Available Metrics</h4>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>
                    <strong>Match Rate:</strong> Percentage of records forming
                    sessions
                  </li>
                  <li>
                    <strong>Avg Duration:</strong> Average parking session
                    length
                  </li>
                  <li>
                    <strong>Total Sessions:</strong> Count of completed sessions
                  </li>
                  <li>
                    <strong>Orphan Rate:</strong> Percentage of unmatched
                    records
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">View Modes</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  <strong>Overall:</strong> Single trend line showing
                  system-wide performance
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>By Zone:</strong> Compare performance across all 10
                  locations simultaneously
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Problem Plates */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                <CardTitle>Problem Plates Identification</CardTitle>
              </div>
              <CardDescription>
                Find recurring issues for operational action
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">What Are Problem Plates?</h4>
                <p className="text-sm text-muted-foreground">
                  License plates with 5 or more orphaned events indicate
                  systemic issues:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Camera malfunction at specific location</li>
                  <li>Consistent OCR recognition errors</li>
                  <li>Physical obstructions blocking camera view</li>
                  <li>Damaged or non-standard license plates</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Taking Action</h4>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Export Problem Plates CSV from Dashboard</li>
                  <li>Review which locations have most issues</li>
                  <li>Dispatch maintenance to check cameras</li>
                  <li>Track if issue resolves after maintenance</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Orphans Management */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                <CardTitle>Orphans Management</CardTitle>
              </div>
              <CardDescription>
                Understanding and managing unmatched records
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Orphan Entries</h4>
                <p className="text-sm text-muted-foreground">
                  Entry events without corresponding exits. Common causes:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>
                    <strong>Vehicles still parked</strong> (most common -
                    normal)
                  </li>
                  <li>Exit camera malfunction</li>
                  <li>
                    Plate changed between entry/exit (obscured, different angle)
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Orphan Exits</h4>
                <p className="text-sm text-muted-foreground">
                  Exit events without corresponding entries. Common causes:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Entry camera malfunction</li>
                  <li>Plate changed between entry/exit</li>
                  <li>
                    Data uploaded out of sequence (system re-checks last 7 days)
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Normal Orphan Levels</h4>
                <p className="text-sm text-muted-foreground">
                  5-10% orphan rate is normal. Higher rates indicate:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Camera issues requiring maintenance</li>
                  <li>Environmental factors (sun glare, weather)</li>
                  <li>Physical obstructions</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* UNDERSTANDING DATA TAB */}
      {activeTab === "metrics" && (
        <div className="space-y-4">
          {/* KPI Explanations */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                <CardTitle>Key Performance Indicators</CardTitle>
              </div>
              <CardDescription>
                What each metric means and how it's calculated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Total Sessions</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Definition:</strong> Number of matched entry-exit
                  pairs (completed parking sessions)
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Calculation:</strong> Count of all sessions where
                  entry and exit were successfully matched
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Match Rate</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Definition:</strong> Percentage of all LPR records
                  that successfully formed sessions
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Calculation:</strong> (Total Sessions / Total Records)
                  × 100
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Good Range:</strong> 90-95% indicates healthy system
                  performance
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Orphan Entries</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Definition:</strong> Entry events that don't have
                  corresponding exits
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Calculation:</strong> Count of all entries where
                  is_orphan = true
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Note:</strong> Increases throughout the day as
                  vehicles enter and park (normal)
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Orphan Exits</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Definition:</strong> Exit events that don't have
                  corresponding entries
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Calculation:</strong> Count of all exits where
                  is_orphan = true
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Note:</strong> Should remain low - high numbers
                  indicate camera/data issues
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Average Duration</h4>
                <p className="text-sm text-muted-foreground">
                  <strong>Definition:</strong> Mean time between entry and exit
                  for all sessions
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Calculation:</strong> Sum of all session durations /
                  Total sessions
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Uses:</strong> Identify parking behavior patterns,
                  optimize lot turnover
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Match Types */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                <CardTitle>Match Types Explained</CardTitle>
              </div>
              <CardDescription>
                Understanding exact vs fuzzy matching
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Exact Match</h4>
                <p className="text-sm text-muted-foreground">
                  Entry and exit plates match character-for-character at the
                  same location.
                </p>
                <div className="bg-muted p-3 rounded text-sm mt-2">
                  <strong>Example:</strong> Entry "ABC123" → Exit "ABC123" at
                  Location 501
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Matching Windows:</strong>
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Same day (Entry Day X → Exit Day X)</li>
                  <li>Overnight (Entry Day X → Exit Day X+1)</li>
                  <li>Multi-day (Entry Day X → Exit Day X+2 through X+7)</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Fuzzy Match</h4>
                <p className="text-sm text-muted-foreground">
                  Entry and exit plates differ by up to 2 characters due to OCR
                  confusion at the same location.
                </p>
                <div className="bg-muted p-3 rounded text-sm mt-2">
                  <strong>Example:</strong> Entry "ABC1O3" → Exit "ABC103" at
                  Location 501
                  <br />
                  <span className="text-xs">(O confused with 0)</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Character Confusions:</strong>
                </p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>O ↔ 0 ↔ Q</li>
                  <li>I ↔ 1 ↔ L</li>
                  <li>B ↔ 8</li>
                  <li>S ↔ 5</li>
                  <li>Z ↔ 2</li>
                  <li>G ↔ 6</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-2">
                  <strong>Constraint:</strong> Maximum 2 character confusions
                  per match (prevents false positives)
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded text-sm">
                <strong>💡 Why Fuzzy Matching Matters:</strong> OCR systems
                often misread similar-looking characters, especially in poor
                lighting or at angles. Fuzzy matching recovers 2-5% of sessions
                that would otherwise be orphaned.
              </div>
            </CardContent>
          </Card>

          {/* Data States */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <CardTitle>Data Processing States</CardTitle>
              </div>
              <CardDescription>
                How records flow through the system
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Phase 1: Exact Matching</h4>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Upload CSV file (entries or exits)</li>
                  <li>Parse and validate records</li>
                  <li>Insert into database as orphans</li>
                  <li>Run exact matching algorithm day-by-day</li>
                  <li>Create sessions for exact matches</li>
                  <li>Mark matched records as non-orphan</li>
                </ol>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Phase 2: Fuzzy Matching</h4>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Identify remaining orphans</li>
                  <li>Apply character confusion matrix</li>
                  <li>Find fuzzy matches within 7-day window</li>
                  <li>Create sessions for fuzzy matches</li>
                  <li>Mark matched records as non-orphan</li>
                </ol>
              </div>
              <div>
                <h4 className="font-semibold mb-2">Extended Lookback</h4>
                <p className="text-sm text-muted-foreground">
                  When processing new uploads, the system automatically
                  re-checks the last 7 days of orphans. This handles:
                </p>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Out-of-order data uploads</li>
                  <li>Late-arriving records</li>
                  <li>Manual backfills</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TROUBLESHOOTING TAB */}
      {activeTab === "troubleshooting" && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                <CardTitle>Common Issues & Solutions</CardTitle>
              </div>
              <CardDescription>
                Troubleshooting guide for frequent scenarios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Issue 1 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: Match Rate Suddenly Dropped Below 85%
                </h4>
                <p className="text-sm font-semibold mb-1">Possible Causes:</p>
                <ul className="list-disc list-inside text-sm space-y-1 mb-2">
                  <li>Camera malfunction (entry or exit)</li>
                  <li>Environmental obstruction (parked vehicle, weather)</li>
                  <li>Data upload issue (missing files)</li>
                </ul>
                <p className="text-sm font-semibold mb-1">Solutions:</p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>
                    Check which location has increased orphans (use zone filter)
                  </li>
                  <li>
                    Review Problem Plates - are they concentrated at one
                    location?
                  </li>
                  <li>Verify all daily CSV files were uploaded</li>
                  <li>Dispatch maintenance to check cameras</li>
                </ol>
              </div>

              {/* Issue 2 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: High Orphan Exits for Specific Location
                </h4>
                <p className="text-sm font-semibold mb-1">Possible Causes:</p>
                <ul className="list-disc list-inside text-sm space-y-1 mb-2">
                  <li>Entry camera at that location is failing</li>
                  <li>Physical obstruction blocking entry camera</li>
                </ul>
                <p className="text-sm font-semibold mb-1">Solutions:</p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Use zone filter to isolate the problem location</li>
                  <li>Go to Orphans page → Filter by that location</li>
                  <li>Note the camera ID from orphan exits</li>
                  <li>Check corresponding entry camera for issues</li>
                  <li>Dispatch maintenance immediately (lost revenue)</li>
                </ol>
              </div>

              {/* Issue 3 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: Same Plate Shows Up in Problem Plates Daily
                </h4>
                <p className="text-sm font-semibold mb-1">Possible Causes:</p>
                <ul className="list-disc list-inside text-sm space-y-1 mb-2">
                  <li>Damaged or non-standard license plate</li>
                  <li>Vanity plate with unusual formatting</li>
                  <li>Camera angle issue for specific parking spot</li>
                </ul>
                <p className="text-sm font-semibold mb-1">Solutions:</p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Manually verify plate in parking lot</li>
                  <li>Check if plate is damaged, obscured, or non-standard</li>
                  <li>Consider manual billing for this vehicle</li>
                  <li>Document as known issue for customer service</li>
                </ol>
              </div>

              {/* Issue 4 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: Upload Processing Takes Longer Than Expected
                </h4>
                <p className="text-sm font-semibold mb-1">
                  Expected Processing Times:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 mb-2">
                  <li>Daily upload (1,000-5,000 records): 5-10 seconds</li>
                  <li>
                    Weekly backfill (10,000-30,000 records): 30-60 seconds
                  </li>
                  <li>Multi-month backfill (100,000+ records): 2-5 minutes</li>
                </ul>
                <p className="text-sm font-semibold mb-1">
                  If Processing is Slower:
                </p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Check file size - extremely large files take longer</li>
                  <li>Don't refresh the page - watch progress updates</li>
                  <li>
                    System is still working if progress percentage increases
                  </li>
                  <li>
                    If stuck at one percentage for 5+ minutes, contact support
                  </li>
                </ol>
              </div>

              {/* Issue 5 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: Uploaded Data Out of Order (Friday before Thursday)
                </h4>
                <p className="text-sm font-semibold mb-1">Not a Problem!</p>
                <p className="text-sm text-muted-foreground mb-2">
                  The system automatically handles out-of-order uploads with
                  7-day lookback:
                </p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Upload files in any order you receive them</li>
                  <li>System automatically re-checks last 7 days of orphans</li>
                  <li>Matches will be created regardless of upload sequence</li>
                  <li>No manual intervention needed</li>
                </ol>
              </div>

              {/* Issue 6 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: Dashboard Shows No Data
                </h4>
                <p className="text-sm font-semibold mb-1">Possible Causes:</p>
                <ul className="list-disc list-inside text-sm space-y-1 mb-2">
                  <li>Date range filter set to period with no data</li>
                  <li>Zone filter excluding all data</li>
                  <li>No data uploaded yet</li>
                </ul>
                <p className="text-sm font-semibold mb-1">Solutions:</p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Check date range filter - expand to wider range</li>
                  <li>Clear zone filter to see all locations</li>
                  <li>Verify data has been uploaded (check Upload page)</li>
                </ol>
              </div>

              {/* Issue 7 */}
              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Issue: CSV Export Missing Some Records
                </h4>
                <p className="text-sm font-semibold mb-1">
                  Check Active Filters:
                </p>
                <p className="text-sm text-muted-foreground mb-2">
                  CSV exports respect your current date range and zone filters.
                </p>
                <p className="text-sm font-semibold mb-1">Solutions:</p>
                <ol className="list-decimal list-inside text-sm space-y-1">
                  <li>Clear all filters to export complete dataset</li>
                  <li>Or adjust filters to include desired records</li>
                  <li>Verify record count before exporting</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Best Practices */}
          <Card>
            <CardHeader>
              <CardTitle>Best Practices</CardTitle>
              <CardDescription>
                Tips for optimal system operation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Upload daily:</strong> Process data as soon as
                    available for timely issue detection
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Review Problem Plates weekly:</strong> Export and
                    share with maintenance team
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Monitor match rate:</strong> Set up alerts if it
                    drops below 90%
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Use zone filters:</strong> Compare location
                    performance to identify underperforming sites
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Export regularly:</strong> Generate weekly reports
                    for stakeholders
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Track trends:</strong> Use Performance Trends to
                    identify seasonal patterns
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    ✓
                  </span>
                  <span className="text-sm">
                    <strong>Investigate orphan spikes:</strong> Sudden increases
                    usually indicate camera issues
                  </span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
